import React, { ReactNode, createContext, useCallback, useMemo, useState } from 'react';

import { getCookie, setCookie } from '@smart-tv/utils/cookie';

const COOKIE_NAME = 'cookies-accepted';
const CONSENT_DAYS = 365;

export interface CookieConsentContextProps {
  children: ReactNode | ReactNode[];
}

export interface CookieConsentContextValues {
  cookiesAccepted?: boolean;
  handleAcceptCookies: () => void;
  handleRejectCookies: () => void;
}

export const CookieConsentContext = createContext<CookieConsentContextValues>(
  {} as CookieConsentContextValues
);

export const CookieConsentConsumer = CookieConsentContext.Consumer;

export const CookieConsentProvider = (props: CookieConsentContextProps) => {
  const { children } = props;

  const [consent, setConsent] = useState(() => getCookie(COOKIE_NAME));

  const handleAcceptCookies = useCallback(() => {
    setConsent('true');
    setCookie(COOKIE_NAME, 'true', { days: CONSENT_DAYS });
  }, []);

  const handleRejectCookies = useCallback(() => {
    setConsent('false');
    setCookie(COOKIE_NAME, 'false', { days: CONSENT_DAYS });
  }, []);

  const value = useMemo(
    () => ({
      cookiesAccepted: consent === null ? undefined : consent === 'true',
      handleAcceptCookies,
      handleRejectCookies
    }),
    [consent, handleAcceptCookies, handleRejectCookies]
  );

  return <CookieConsentContext.Provider value={value}>{children}</CookieConsentContext.Provider>;
};

export default CookieConsentContext;
